import { ReactNode, RefObject } from 'react';
import {
  Drawer, Box, Typography, IconButton, Divider, alpha, useTheme, useMediaQuery,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FormActions } from './forms';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';

interface FormDrawerProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  loading?: boolean;
  disableSubmit?: boolean;
  children: ReactNode;
  contentRef?: RefObject<HTMLDivElement | null>;
  width?: number | string;
  submitHint?: string;
}

export default function FormDrawer({
  open,
  title,
  subtitle,
  onClose,
  onSubmit,
  submitLabel = 'Save',
  loading = false,
  disableSubmit = false,
  children,
  width = 520,
  contentRef,
  submitHint,
}: FormDrawerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useI18n();
  const drawerAnchor = theme.direction === 'rtl' ? 'left' : 'right';

  return (
    <Drawer
      anchor={drawerAnchor}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : width,
          maxWidth: '100vw',
          height: '100%',
          maxHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          borderInlineStart: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.palette.mode === 'light'
            ? '-16px 0 40px rgba(11,18,32,0.08)'
            : '-16px 0 40px rgba(0,0,0,0.4)',
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
          borderTop: `3px solid ${theme.palette.primary.main}`,
          bgcolor: 'background.paper',
        }}
      >
        <Box minWidth={0}>
          <Typography variant="h6" fontWeight={650} letterSpacing="-0.025em" lineHeight={1.3}>
            {translateUiText(title, t)}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ lineHeight: 1.5 }}>
              {translateUiText(subtitle, t)}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ mt: -0.25, bgcolor: alpha(theme.palette.text.primary, 0.04) }}
          aria-label={t('common.action.close', 'Close')}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Box
        ref={contentRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          overflowX: 'hidden',
          px: 3,
          py: 2.75,
          pb: 3,
          minHeight: 0,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children}
      </Box>

      {onSubmit && (
        <>
          <Divider sx={{ flexShrink: 0 }} />
          <Box
            sx={{
              flexShrink: 0,
              px: 3,
              py: 2,
              pb: 'max(16px, env(safe-area-inset-bottom))',
              bgcolor: alpha(theme.palette.text.primary, 0.015),
            }}
          >
            <FormActions
              onCancel={onClose}
              onSubmit={onSubmit}
              submitLabel={submitLabel}
              loading={loading}
              disableSubmit={disableSubmit}
            />
            {submitHint && (
              <Typography variant="body2" color="text.primary" fontWeight={600} sx={{ mt: 1.25, textAlign: 'right' }}>
                {translateUiText(submitHint, t)}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Drawer>
  );
}
